// src/utils/date.ts
function formatDate(date, locale = "nl-BE") {
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleDateString(locale);
}
function formatDateTime(date, locale = "nl-BE") {
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleString(locale);
}
function formatTime(date, locale = "nl-BE") {
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
}
function getTimeOfDayGreeting(hour) {
  const h = hour ?? (/* @__PURE__ */ new Date()).getHours();
  if (h >= 5 && h < 12) return "morning";
  if (h >= 12 && h < 17) return "afternoon";
  if (h >= 17 && h < 21) return "evening";
  return "night";
}
function formatDuration(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

// src/utils/format.ts
function formatFileSize(bytes) {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}
function truncateText(text, maxLength) {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + "...";
}
function capitalize(str) {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
}

// src/utils/tts.ts
function resolveLang(explicit) {
  if (explicit) return explicit;
  if (typeof document !== "undefined") {
    const htmlLang = document.documentElement?.lang;
    if (htmlLang) return htmlLang;
  }
  if (typeof navigator !== "undefined" && navigator.language) {
    return navigator.language;
  }
  return "en-US";
}
function speak(text, opts = {}) {
  return new Promise((resolve, reject) => {
    if (typeof window === "undefined" || !("speechSynthesis" in window)) {
      reject(new Error("SPEECH_SYNTHESIS_UNAVAILABLE"));
      return;
    }
    let utterance;
    try {
      window.speechSynthesis.cancel();
      utterance = new SpeechSynthesisUtterance(text);
    } catch (err) {
      reject(err instanceof Error ? err : new Error("SPEECH_SYNTHESIS_CONSTRUCT_FAILED"));
      return;
    }
    utterance.lang = resolveLang(opts.lang);
    utterance.rate = opts.rate ?? 0.9;
    utterance.volume = opts.volume ?? 1;
    utterance.pitch = opts.pitch ?? 1;
    try {
      const voices = window.speechSynthesis.getVoices?.();
      if (voices && voices.length > 0) {
        const prefix = utterance.lang.split("-")[0]?.toLowerCase();
        const hasMatch = voices.some((v) => v.lang?.toLowerCase().startsWith(prefix ?? ""));
        if (!hasMatch) {
          console.warn(
            `[caro/utils/tts] No voice installed for lang "${utterance.lang}"; browser will pick a default voice.`
          );
        }
      }
    } catch {
    }
    utterance.onend = () => resolve();
    utterance.onerror = (event) => {
      const errCode = event?.error ?? "SPEECH_SYNTHESIS_ERROR";
      reject(new Error(`SPEECH_SYNTHESIS_ERROR:${errCode}`));
    };
    try {
      window.speechSynthesis.speak(utterance);
    } catch (err) {
      reject(err instanceof Error ? err : new Error("SPEECH_SYNTHESIS_SPEAK_FAILED"));
    }
  });
}
var cachedAudioContext = null;
function getAudioContextCtor() {
  if (typeof window === "undefined") return null;
  const w = window;
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}
function getAudioContext() {
  if (cachedAudioContext && cachedAudioContext.state !== "closed") {
    return cachedAudioContext;
  }
  const Ctor = getAudioContextCtor();
  if (!Ctor) return null;
  try {
    cachedAudioContext = new Ctor();
    return cachedAudioContext;
  } catch {
    cachedAudioContext = null;
    return null;
  }
}
function beep(opts = {}) {
  return new Promise((resolve, reject) => {
    const ctx = getAudioContext();
    if (!ctx) {
      reject(new Error("AUDIO_CONTEXT_UNAVAILABLE"));
      return;
    }
    const frequency = opts.frequency ?? 880;
    const durationMs = opts.durationMs ?? 200;
    const peakVolume = opts.volume ?? 0.4;
    const attackSec = 0.02;
    const releaseSec = 0.05;
    const durationSec = durationMs / 1e3;
    const sustainSec = Math.max(durationSec - attackSec - releaseSec, 0.01);
    const totalSec = attackSec + sustainSec + releaseSec;
    let oscillator;
    let gain;
    try {
      oscillator = ctx.createOscillator();
      gain = ctx.createGain();
      oscillator.type = "sine";
      oscillator.frequency.value = frequency;
      oscillator.connect(gain);
      gain.connect(ctx.destination);
      const now = ctx.currentTime;
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(peakVolume, now + attackSec);
      gain.gain.setValueAtTime(peakVolume, now + attackSec + sustainSec);
      gain.gain.linearRampToValueAtTime(0, now + totalSec);
      oscillator.start(now);
      oscillator.stop(now + totalSec);
    } catch (err) {
      reject(err instanceof Error ? err : new Error("AUDIO_BEEP_FAILED"));
      return;
    }
    let settled = false;
    const settle = () => {
      if (settled) return;
      settled = true;
      try {
        oscillator.disconnect();
        gain.disconnect();
      } catch {
      }
      resolve();
    };
    oscillator.onended = settle;
    setTimeout(settle, totalSec * 1e3 + 100);
  });
}
async function speakOrBeep(text, opts) {
  try {
    await speak(text, opts);
    return "spoken";
  } catch {
    try {
      await beep();
      return "beeped";
    } catch {
      return "failed";
    }
  }
}
function __resetAudioContextForTests() {
  if (cachedAudioContext) {
    try {
      void cachedAudioContext.close();
    } catch {
    }
  }
  cachedAudioContext = null;
}
var SITE_CUSTOM_FIELDS = {
  SITE_MANAGER_EMAIL: "site_manager_email",
  SITE_MANAGER_USER_ID: "site_manager_user_id",
  // Repair-estimates / invoicing custom fields (20260702-repair-estimates-catalog).
  // Read via `@caro/shared/utils/invoicing` helpers, mirroring getSiteManagerEmail.
  INVOICING_EMAIL: "invoicing_mail",
  // invoicing party's email (LD-INVOICE-MAIL)
  CLIENT_TAX_PROFILE: "tax_profile",
  // client tax-profile code (LD-TAX, CR-8)
  SERVICE_CONTRACT: "service_contract"
  // boolean flag: site under a service contract; gates Flow B (CR-12)
};

// src/utils/siteManager.ts
var LEGACY_RESPONSIBLE_EMAIL_FIELD = "responsibleEmail";
function getSiteManagerEmail(location) {
  const data = Array.isArray(location?.data) ? location.data : [];
  const primary = data.find((f) => f?.name === SITE_CUSTOM_FIELDS.SITE_MANAGER_EMAIL && f?.value);
  if (primary) return primary.value;
  const legacy = data.find((f) => f?.name === LEGACY_RESPONSIBLE_EMAIL_FIELD && f?.value);
  return legacy ? legacy.value : null;
}
function getSiteManagerUserId(location) {
  const data = Array.isArray(location?.data) ? location.data : [];
  const field = data.find((f) => f?.name === SITE_CUSTOM_FIELDS.SITE_MANAGER_USER_ID);
  return field?.value || null;
}
function siteHasManager(location) {
  return Boolean(getSiteManagerEmail(location));
}
function withSiteManagerFields(data, { email, userId }) {
  const others = (Array.isArray(data) ? data : []).filter(
    (f) => f?.name !== SITE_CUSTOM_FIELDS.SITE_MANAGER_EMAIL && f?.name !== SITE_CUSTOM_FIELDS.SITE_MANAGER_USER_ID
  );
  if (!email) return others;
  return [
    ...others,
    {
      name: SITE_CUSTOM_FIELDS.SITE_MANAGER_EMAIL,
      value: email,
      type: "text",
      visible: true,
      permissionLevel: "authenticated"
    },
    {
      name: SITE_CUSTOM_FIELDS.SITE_MANAGER_USER_ID,
      value: String(userId ?? ""),
      type: "text",
      visible: false,
      permissionLevel: "admin"
    }
  ];
}

// src/utils/invoicing.ts
function getInvoicingEmail(location) {
  const data = Array.isArray(location?.data) ? location.data : [];
  const field = data.find((f) => f?.name === SITE_CUSTOM_FIELDS.INVOICING_EMAIL && f?.value);
  return field ? field.value : null;
}
function getClientTaxProfile(location) {
  const data = Array.isArray(location?.data) ? location.data : [];
  const field = data.find((f) => f?.name === SITE_CUSTOM_FIELDS.CLIENT_TAX_PROFILE && f?.value);
  return field ? field.value : null;
}
function isServiceContractSite(location) {
  const data = Array.isArray(location?.data) ? location.data : [];
  const field = data.find((f) => f?.name === SITE_CUSTOM_FIELDS.SERVICE_CONTRACT);
  if (!field) return false;
  const raw = field.value;
  if (typeof raw === "boolean") return raw;
  const norm = String(raw ?? "").trim().toLowerCase();
  return norm === "true" || norm === "1" || norm === "yes" || norm === "on";
}
function resolveLineTax(taxCategory, taxProfile, matrix) {
  const rows = Array.isArray(matrix) ? matrix : [];
  const match = rows.find(
    (r) => r?.tax_category === taxCategory && r?.tax_profile === taxProfile && r?.active !== false && r?.active !== 0
  );
  if (!match) {
    throw new Error(
      `resolveLineTax: no active tax_rate for (tax_category=${String(
        taxCategory
      )} \xD7 tax_profile=${String(taxProfile)}). Refusing to offer/finalize (fail-closed, NFR-5).`
    );
  }
  return {
    tax_rate_bps: match.rate_bps,
    legal_mention_key: match.legal_mention_key ?? null
  };
}
function computeLineTaxCents(unitPriceCents, quantity, taxRateBps) {
  return Math.round(unitPriceCents * quantity * taxRateBps / 1e4);
}

export { __resetAudioContextForTests, beep, capitalize, computeLineTaxCents, formatDate, formatDateTime, formatDuration, formatFileSize, formatTime, getClientTaxProfile, getInvoicingEmail, getSiteManagerEmail, getSiteManagerUserId, getTimeOfDayGreeting, isServiceContractSite, resolveLineTax, siteHasManager, speak, speakOrBeep, truncateText, withSiteManagerFields };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map